import React, { useState, useEffect, useCallback } from 'react';
import { Cloud, Brain, Key, Eye, EyeOff, Save, X, Check, AlertTriangle, CheckCircle, Settings2, Wrench, ExternalLink, MapPin, Waves, Globe, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../../utils/api';


const CATEGORY_META = {
  ai: { label: 'AI Services', icon: Brain, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20' },
  weather: { label: 'Weather & Ocean Data', icon: Cloud, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20' },
  surf: { label: 'Surf Forecasts', icon: Waves, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  utility: { label: 'Utility', icon: Globe, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'text-green-400', bg: 'bg-green-500/15', icon: CheckCircle },
  maintenance: { label: 'Maintenance', color: 'text-yellow-400', bg: 'bg-yellow-500/15', icon: AlertTriangle },
  not_configured: { label: 'No Key', color: 'text-red-400/70', bg: 'bg-red-500/10', icon: Key },
  planned: { label: 'Inactive', color: 'text-muted-foreground/50', bg: 'bg-white/5', icon: Settings2 },
};

function SourceCard({ source, onUpdateKey, onToggleMaintenance }) {
  const [showKey, setShowKey] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [reason, setReason] = useState(source.maintenance_reason || '');
  const [togglingMaint, setTogglingMaint] = useState(false);

  const statusCfg = STATUS_CONFIG[source.status] || STATUS_CONFIG.planned;
  const StatusIcon = statusCfg.icon;

  const handleSaveKey = async () => {
    if (!newKey.trim()) return;
    setSaving(true);
    const ok = await onUpdateKey(source.id, newKey.trim());
    setSaving(false);
    if (ok) { setEditing(false); setNewKey(''); }
  };

  const handleToggleMaintenance = async (enable) => {
    setTogglingMaint(true);
    await onToggleMaintenance(source.id, enable, reason);
    setTogglingMaint(false);
    setShowMaintenance(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] border border-white/5 rounded-xl p-3.5 space-y-2"
      data-testid={`source-card-${source.id}`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-xs font-bold text-foreground/90 truncate">{source.name}</p>
            {source.required && (
              <span className="text-[7px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-bold flex-shrink-0">CORE</span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground/50 leading-relaxed">{source.description}</p>
        </div>
        <div className={`inline-flex items-center gap-1 text-[9px] font-medium px-2 py-1 rounded-lg flex-shrink-0 ${statusCfg.bg} ${statusCfg.color}`}>
          <StatusIcon className="w-2.5 h-2.5" />
          {statusCfg.label}
        </div>
      </div>

      {/* Key section */}
      {source.requires_key && (
        <div className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-2.5">
          {editing ? (
            <div className="space-y-2" data-testid={`source-key-editor-${source.id}`}>
              <input
                type="text"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                placeholder="Paste new API key..."
                autoFocus
                className="w-full bg-white/[0.04] border border-primary/20 rounded-lg px-2.5 py-2 text-[11px] font-mono outline-none focus:border-primary/40 transition-colors"
                data-testid={`source-key-input-${source.id}`}
              />
              <div className="flex gap-1.5">
                <button onClick={() => { setEditing(false); setNewKey(''); }} className="flex-1 bg-white/5 border border-white/10 text-[9px] py-1.5 rounded-lg font-medium transition-colors hover:border-white/20" data-testid={`source-key-cancel-${source.id}`}>
                  Cancel
                </button>
                <button
                  onClick={handleSaveKey}
                  disabled={saving || !newKey.trim()}
                  className="flex-1 bg-primary/15 border border-primary/25 text-primary text-[9px] py-1.5 rounded-lg font-bold transition-colors hover:bg-primary/25 disabled:opacity-40 inline-flex items-center justify-center gap-1"
                  data-testid={`source-key-save-${source.id}`}
                >
                  <Save className="w-2.5 h-2.5" />
                  {saving ? 'Saving...' : 'Save Key'}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Key className="w-3 h-3 text-muted-foreground/30 flex-shrink-0" />
              {source.has_key ? (
                <>
                  <span className="text-[10px] font-mono text-muted-foreground/50 flex-1 truncate">
                    {showKey ? source.key_masked : '••••••••••••••••'}
                  </span>
                  <button onClick={() => setShowKey(!showKey)} className="p-1 hover:bg-white/5 rounded transition-colors" data-testid={`source-key-toggle-${source.id}`}>
                    {showKey ? <EyeOff className="w-3 h-3 text-muted-foreground/30" /> : <Eye className="w-3 h-3 text-muted-foreground/30" />}
                  </button>
                </>
              ) : (
                <span className="text-[10px] text-red-400/50 italic flex-1">No key configured</span>
              )}
              <button
                onClick={() => setEditing(true)}
                className="text-[9px] bg-white/5 border border-white/10 hover:border-primary/30 text-muted-foreground/60 hover:text-primary px-2 py-1 rounded-lg font-medium transition-all flex-shrink-0"
                data-testid={`source-key-edit-${source.id}`}
              >
                {source.has_key ? 'Update' : 'Add Key'}
              </button>
            </div>
          )}
          {source.has_key && source.key_source && (
            <p className="text-[8px] text-muted-foreground/25 mt-1.5">
              Source: {source.key_source} {source.updated_by ? `| last updated by ${source.updated_by}` : ''}
            </p>
          )}
        </div>
      )}

      {/* Maintenance toggle + meta */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-[8px] text-muted-foreground/25">
          {source.last_success && <span>Last OK: {new Date(source.last_success).toLocaleTimeString()}</span>}
          {source.last_fail && <span className="text-red-400/30">Last fail: {new Date(source.last_fail).toLocaleTimeString()}</span>}
          {source.fail_count > 0 && <span className="text-red-400/30">{source.fail_count} fails</span>}
          {source.docs_url && (
            <a href={source.docs_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-primary/30 hover:text-primary/60 transition-colors">
              <ExternalLink className="w-2 h-2" /> Docs
            </a>
          )}
        </div>
        <button
          onClick={() => setShowMaintenance(!showMaintenance)}
          className={`text-[9px] px-2 py-1 rounded-lg font-medium transition-all inline-flex items-center gap-1 ${
            source.maintenance
              ? 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/20 hover:border-yellow-500/40'
              : 'bg-white/[0.03] text-muted-foreground/40 border border-white/[0.03] hover:border-white/10 hover:text-muted-foreground/60'
          }`}
          data-testid={`source-maintenance-toggle-${source.id}`}
        >
          <Wrench className="w-2.5 h-2.5" />
          {source.maintenance ? 'In Maintenance' : 'Maintenance'}
        </button>
      </div>

      {/* Maintenance panel */}
      <AnimatePresence>
        {showMaintenance && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="bg-yellow-500/[0.04] border border-yellow-500/10 rounded-lg p-2.5 space-y-2" data-testid={`source-maintenance-panel-${source.id}`}>
              <input
                type="text"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="Reason (optional)..."
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-1.5 text-[10px] outline-none focus:border-yellow-500/30 transition-colors"
                data-testid={`source-maintenance-reason-${source.id}`}
              />
              <div className="flex gap-1.5">
                <button
                  onClick={() => setShowMaintenance(false)}
                  className="flex-1 bg-white/5 border border-white/10 text-[9px] py-1.5 rounded-lg font-medium"
                >
                  Cancel
                </button>
                {source.maintenance ? (
                  <button
                    onClick={() => handleToggleMaintenance(false)}
                    disabled={togglingMaint}
                    className="flex-1 bg-green-500/15 border border-green-500/25 text-green-400 text-[9px] py-1.5 rounded-lg font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40"
                    data-testid={`source-maintenance-disable-${source.id}`}
                  >
                    <Check className="w-2.5 h-2.5" />
                    {togglingMaint ? 'Saving...' : 'Mark Active'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleToggleMaintenance(true)}
                    disabled={togglingMaint}
                    className="flex-1 bg-yellow-500/15 border border-yellow-500/25 text-yellow-400 text-[9px] py-1.5 rounded-lg font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40"
                    data-testid={`source-maintenance-enable-${source.id}`}
                  >
                    <AlertTriangle className="w-2.5 h-2.5" />
                    {togglingMaint ? 'Saving...' : 'Set Maintenance'}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Maintenance reason display */}
      {source.maintenance && source.maintenance_reason && !showMaintenance && (
        <p className="text-[9px] text-yellow-400/40 italic px-1">Reason: {source.maintenance_reason}</p>
      )}
    </motion.div>
  );
}

export default function AdminSources() {
  const [sources, setSources] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});

  const fetchSources = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/admin/sources`);
      if (res.ok) {
        const data = await res.json();
        setSources(data.sources || []);
        setCounts(data.counts || {});
        // Auto-expand all categories
        const cats = {};
        (data.sources || []).forEach(s => { cats[s.category] = true; });
        setExpandedCats(prev => ({ ...cats, ...prev }));
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const handleUpdateKey = async (sourceId, keyValue) => {
    try {
      const res = await authFetch(`${API}/api/admin/sources/${sourceId}/key`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ key_value: keyValue }),
      });
      if (res.ok) { fetchSources(); return true; }
    } catch {}
    return false;
  };

  const handleToggleMaintenance = async (sourceId, enable, reason) => {
    try {
      await authFetch(`${API}/api/admin/sources/${sourceId}/maintenance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ maintenance: enable, reason }),
      });
      fetchSources();
    } catch {}
  };

  const toggleCat = (cat) => {
    setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Group by category
  const grouped = {};
  sources.forEach(s => {
    if (!grouped[s.category]) grouped[s.category] = [];
    grouped[s.category].push(s);
  });
  const catOrder = ['ai', 'weather', 'surf', 'utility'];
  const orderedCats = catOrder.filter(c => grouped[c]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex space-x-1.5">
          <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
          <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
          <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-sources">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 className="w-3.5 h-3.5 text-primary/60" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground/50 font-medium">API Sources</span>
        </div>
        <div className="flex items-center gap-1.5">
          {counts.active > 0 && (
            <span className="text-[8px] bg-green-500/10 text-green-400 px-1.5 py-0.5 rounded-md font-medium">{counts.active} active</span>
          )}
          {counts.maintenance > 0 && (
            <span className="text-[8px] bg-yellow-500/10 text-yellow-400 px-1.5 py-0.5 rounded-md font-medium">{counts.maintenance} maintenance</span>
          )}
          {counts.not_configured > 0 && (
            <span className="text-[8px] bg-red-500/10 text-red-400/60 px-1.5 py-0.5 rounded-md font-medium">{counts.not_configured} no key</span>
          )}
        </div>
      </div>

      {/* Category groups */}
      {orderedCats.map(cat => {
        const meta = CATEGORY_META[cat] || CATEGORY_META.utility;
        const CatIcon = meta.icon;
        const catSources = grouped[cat] || [];
        const isExpanded = expandedCats[cat] !== false;

        return (
          <div key={cat}>
            <button
              onClick={() => toggleCat(cat)}
              className="w-full flex items-center gap-2 py-2 px-1 hover:bg-white/[0.02] rounded-lg transition-colors"
              data-testid={`sources-cat-${cat}`}
            >
              {isExpanded ? <ChevronDown className="w-3 h-3 text-muted-foreground/30" /> : <ChevronRight className="w-3 h-3 text-muted-foreground/30" />}
              <CatIcon className={`w-3 h-3 ${meta.color} opacity-60`} />
              <span className={`text-[10px] font-bold tracking-wide ${meta.color} opacity-80`}>{meta.label}</span>
              <span className="text-[8px] text-muted-foreground/20 ml-auto">{catSources.length}</span>
            </button>

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  <div className="space-y-2 pl-1 pb-2">
                    {catSources.map(source => (
                      <SourceCard
                        key={source.id}
                        source={source}
                        onUpdateKey={handleUpdateKey}
                        onToggleMaintenance={handleToggleMaintenance}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      {/* Footer */}
      <div className="text-center pt-2 border-t border-white/[0.03]">
        <p className="text-[8px] text-muted-foreground/20">
          {sources.length} source{sources.length !== 1 ? 's' : ''} | Keys stored in DB override .env values
        </p>
      </div>
    </div>
  );
}
